import { useBunja } from 'bunja/react';
import { differenceInDays, isBefore, isSameDay } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { Tooltip } from '@flipster/react/design-system-legacy/tooltip';
import { Badge, BadgeProps } from '@flipster/react/design-system/badge';
import { CountdownDate } from '@flipster/react/design-utils/countdown-date';
import Formatted from '@flipster/react/design-utils/formatted';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { divider, flex, hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import { convertDate, formatDatetime } from '@flipster/utils/datetime';
import segment from '@flipster/utils/segment';
import { EarnEvent } from '@flipster/utils/segment/events/earn';

import { EventStatus, userPoolBunja } from '../user-pool.state';
import { showAlert } from '../utils';

interface PoolStatusCountdownProps {
  status: EventStatus;
  startTime: string;
  endTime: string;
}

const PoolStatusCountdown = ({ status, startTime, endTime }: PoolStatusCountdownProps) => {
  if (status === 'Closed') return null;

  if (status === 'Upcoming') {
    const startDate = convertDate(startTime);
    const today = new Date();
    if (!startDate) return null;

    const daysLeft = Math.max(1, differenceInDays(startDate, today));
    return (
      <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
        Opening in {daysLeft} day{daysLeft > 1 ? 's' : ''}
      </p>
    );
  }

  return (
    <CountdownDate
      targetDate={endTime}
      format='${dd}D ${hh}H ${mm}M ${ss}S left'
      css={{ textStyle: 'Text/M/Regular', color: 'content.default.level-2' }}
    />
  );
};

const PoolInfoCard = () => {
  const {
    userPoolDataAtom,
    eventStatusAtom,
    yourTicketsAtom,
    yourTotalRewardAtom,
    currentTimeAtom,
    estTotalRewardAtom,
  } = useBunja(userPoolBunja);

  const pool = useAtomValue(userPoolDataAtom);
  const status = useAtomValue(eventStatusAtom);
  const yourTickets = useAtomValue(yourTicketsAtom);
  const yourTotalReward = useAtomValue(yourTotalRewardAtom);
  const currentTime = useAtomValue(currentTimeAtom);
  const estTotalReward = useAtomValue(estTotalRewardAtom);
  // Track pool status view
  useEffect(() => {
    if (pool && status) {
      segment.track(EarnEvent.EarnTabViewNewJoinerPoolStatus, {
        pool_status: status,
        estimate_full_reward_usdt: pool.totalReward,
        your_total_reward_usdt: yourTotalReward,
      });
    }
  }, [pool, status, yourTotalReward]);

  if (!pool) return null;

  return (
    <div className={stack({ w: '100%', bg: 'background.default.level-1', rounded: 8, px: 16, pt: 12, pb: 16 })}>
      <TotalRewardPool
        status={status}
        startTime={pool.startTime}
        endTime={pool.endTime}
        totalReward={pool.totalReward}
      />
      <Divider />
      <EstFullReward totalEstimatedReward={estTotalReward} />
      <Divider />
      <TicketInformation
        totalTicketCount={pool.totalTicketCount}
        yourTickets={yourTickets}
        startTime={pool.startTime}
        currentTime={currentTime}
      />
      <Divider />
      <YourTotalReward yourTotalReward={yourTotalReward} status={status} />
    </div>
  );
};

const Divider = () => {
  return <hr className={divider({ orientation: 'horizontal', color: 'border.default.level-1', my: 4, h: 1 })} />;
};

const TotalRewardPool = ({
  status,
  startTime,
  endTime,
  totalReward,
}: {
  status: EventStatus;
  startTime: string;
  endTime: string;
  totalReward: number;
}) => {
  const badgeMeta = getBadgeMeta(status);

  return (
    <div className={stack({ gap: 8, pb: 8 })}>
      <div
        className={hstack({
          justify: 'space-between',
          gap: 8,
        })}
      >
        <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Total_reward_pool' />
        </p>
        <div className={hstack({ gap: 4 })}>
          <Formatted.Number
            value={totalReward}
            toFixed
            precision={UsdtPrecision}
            css={{ textStyle: 'Text/M/Semibold', color: 'content.default.level-5' }}
          />
          <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>USDT</p>
        </div>
      </div>
      <div
        className={flex({
          justify: 'space-between',
          gap: 8,
        })}
      >
        <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Period' />
        </p>
        <div className={hstack({ gap: 4 })}>
          <p
            className={css({
              textStyle: 'Text/M/Regular',
              color: 'content.default.level-5',
              textAlign: 'right',
              whiteSpace: 'pre-wrap',
            })}
          >
            {formatPeriod(startTime, endTime)}
          </p>
        </div>
      </div>
      <div
        className={flex({
          justify: 'space-between',
          gap: 8,
        })}
      >
        <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Status' />
        </p>
        <div
          className={stack({
            align: 'flex-end',
            justify: 'center',
            gap: 4,
          })}
        >
          <Badge type='tint' color={badgeMeta.color}>
            {status}
          </Badge>
          <PoolStatusCountdown status={status} startTime={startTime} endTime={endTime} />
        </div>
      </div>
    </div>
  );
};

const EstFullReward = ({ totalEstimatedReward }: { totalEstimatedReward: number }) => {
  return (
    <div className={flex({ gap: 4, py: 8 })}>
      <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
        <FormattedMessage id='Autogen_common.r251126.s_Est_Full_reward' />
      </p>
      <div
        className={stack({
          align: 'flex-end',
          justify: 'center',
          flex: 1,
        })}
      >
        <Formatted.Number
          value={totalEstimatedReward}
          suffix={UsdtCurrency}
          toFixed
          precision={UsdtPrecision}
          css={{ textStyle: 'Title/XS', color: 'content.state.positive' }}
        />
      </div>
    </div>
  );
};

const TicketInformation = ({
  totalTicketCount,
  yourTickets,
  startTime,
  currentTime,
}: {
  totalTicketCount: number;
  yourTickets: number;
  startTime: string;
  currentTime: number;
}) => {
  const now = new Date(currentTime);
  const startDate = convertDate(startTime)!;
  const today = now;

  const isBeforeStart = isBefore(today, startDate);
  const isStartDate = isSameDay(today, startDate);

  return (
    <div className={stack({ gap: 8, py: 8 })}>
      <div
        className={flex({
          justify: 'space-between',
          gap: 8,
        })}
      >
        <div
          className={stack({
            justify: 'center',
            gap: 4,
          })}
        >
          <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
            <FormattedMessage id='Autogen_common.r251126.s_Total_participant_tickets' />
          </p>
        </div>
        <div className={hstack({ gap: 4 })}>
          <p className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-5' })}>
            {isBeforeStart ? '-' : isStartDate ? 'Calculating...' : totalTicketCount}
          </p>
        </div>
      </div>
      {/* Your total tickets */}
      <div
        className={flex({
          justify: 'space-between',
          gap: 8,
        })}
      >
        <div
          className={stack({
            justify: 'center',
            gap: 4,
          })}
        >
          <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
            <FormattedMessage id='Autogen_common.r251126.s_Your_total_tickets' />
          </p>
        </div>
        <div className={hstack({ gap: 4 })}>
          <p className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-5' })}>
            {isBeforeStart ? '-' : yourTickets}
          </p>
        </div>
      </div>
    </div>
  );
};

const YourTotalReward = ({ yourTotalReward, status }: { yourTotalReward: number; status: EventStatus }) => {
  return (
    <div
      className={stack({
        gap: 4,
        pt: 8,
        w: '100%',
      })}
    >
      <div
        className={flex({
          direction: {
            base: 'column',
            tablet: 'row',
          },
          justify: 'center',
          gap: 12,
        })}
      >
        <div
          className={hstack({
            direction: 'row',
            gap: 4,
          })}
        >
          <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
            <FormattedMessage id='Autogen_common.r251126.s_Your_total_reward' />
          </p>
          <Tooltip
            placement='bottom'
            maxWidth={400}
            mobileAlert={() => {
              showAlert({
                title: 'Your total reward',
                content:
                  '(Total reward in pool) x (Sum of my Tickets) / (Sum of all participants’ Tickets)\n\n The number of total participant tickets and reward you’re seeing may be delayed while calculations occur.',
                confirmText: 'Got it',
              });
            }}
            onOpenChange={(open) => {
              if (!open) return;
              segment.track(EarnEvent.EarnTabClickNewJoinerPoolTotalReward, {
                your_total_reward_usdt: status === 'Upcoming' ? undefined : yourTotalReward,
              });
            }}
          >
            <Tooltip.Trigger asChild>
              <InformationCircleIcon size={16} color={token('colors.content.default.level-2')} />
            </Tooltip.Trigger>
            <Tooltip.Content>
              (Total reward in pool) x (Sum of my Tickets) / (Sum of all participants' Tickets)
              <br />
              <br />
              The number of total participant tickets and reward you're seeing may be delayed while calculations occur.
            </Tooltip.Content>
          </Tooltip>
        </div>
        <div
          className={flex({
            align: 'flex-end',
            justify: 'flex-end',
            gap: 4,
            textStyle: 'Title/S',
            color: 'inverse.white',
          })}
        >
          {status === 'Upcoming' ? (
            <p>-</p>
          ) : (
            <Formatted.Number value={yourTotalReward} toFixed precision={UsdtPrecision} />
          )}
          <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-5' })}>USDT</p>
        </div>
      </div>
    </div>
  );
};

const getBadgeMeta = (status: EventStatus): { color: BadgeProps['color'] } => {
  switch (status) {
    case 'Ongoing':
      return { color: 'positive' };
    case 'Upcoming':
      return { color: 'accent' };
    case 'Closed':
      return { color: 'neutral' };
  }
};

const formatPeriod = (startTimeStr: string, endTimeStr: string) => {
  const startFormatted = formatDatetime(startTimeStr, { format: 'yy.MM.dd HH:mm', isUTC: true });
  const endFormatted = formatDatetime(endTimeStr, { format: 'MM.dd HH:mm', isUTC: true });

  return `${startFormatted}\n- ${endFormatted} UTC`;
};

export default PoolInfoCard;
